import { useFormikContext } from 'formik';

import { useGroups } from '@/react/portainer/environments/environment-groups/queries';

import { Button, LoadingButton } from '@@/buttons';
import { StickyFooter } from '@@/StickyFooter/StickyFooter';

import { PolicyType } from '../types';

import { confirmPolicyWithPreview } from './confirmPolicyWithPreview';

/**
 * Common form values that PolicyFormFooter expects from Formik context.
 * All policy forms must include these fields.
 */
interface PolicyFormFooterValues {
  name: string;
  environmentGroupIds: Array<number>;
}

interface Props {
  /** Form mode - affects button labels and validation behavior */
  mode: 'create' | 'edit';
  /** Policy type - required for the preview modal */
  policyType: PolicyType;
  /** Policy ID - null for create, set for edit mode */
  policyId?: number | null;
  /** Original environment group IDs - for detecting detached groups in edit mode */
  originalEnvironmentGroupIds?: Array<number>;
  /** Wizard back navigation - only used in create mode */
  onBack?: () => void;
  /** Whether back button should be enabled - only used in create mode */
  canGoBack?: boolean;
}

/**
 * Shared footer component for policy forms.
 *
 * This component must be rendered inside a Formik context as it uses
 * useFormikContext to access form state (submitForm, isValid, isSubmitting, dirty).
 *
 * Features:
 * - In create mode: Shows Back button (if onBack provided) + Create button in StickyFooter
 * - In edit mode: Shows only Update button (no StickyFooter, rendered inline in Widget)
 * - Shows PolicyPreviewModal with conflict detection before creating/updating a policy
 * - Handles loading states during submission
 */
export function PolicyFormFooter({
  mode,
  policyType,
  policyId = null,
  originalEnvironmentGroupIds = [],
  onBack,
  canGoBack = false,
}: Props) {
  const { isSubmitting, isValid, dirty, submitForm, values } =
    useFormikContext<PolicyFormFooterValues>();

  const groupsQuery = useGroups();
  const groups = groupsQuery.data || [];

  const submitLabel = mode === 'create' ? 'Create policy' : 'Update policy';
  const loadingLabel = mode === 'create' ? 'Creating...' : 'Updating...';

  // Create: must be valid. Edit: must be valid AND dirty
  const isDisabled = !isValid || isSubmitting || (mode === 'edit' && !dirty);

  const buttons = (
    <>
      {/* Back button - only shown in create mode with wizard */}
      {mode === 'create' && onBack && (
        <Button
          color="default"
          onClick={onBack}
          disabled={!canGoBack}
          data-cy="policy-wizard-back-button"
          size="medium"
        >
          Back
        </Button>
      )}

      <LoadingButton
        color="primary"
        // type button for better control over submit behaviour, instead of automatically calling the formik submit function
        type="button"
        onClick={handleSubmitClick}
        disabled={isDisabled}
        isLoading={isSubmitting}
        loadingText={loadingLabel}
        data-cy={`policy-${mode}-button`}
        size="medium"
      >
        {submitLabel}
      </LoadingButton>
    </>
  );

  // In create mode, use StickyFooter. In edit mode, render inline.
  if (mode === 'create') {
    return <StickyFooter className="justify-end gap-4">{buttons}</StickyFooter>;
  }

  // Edit mode: render inline (to be placed inside Widget)
  return <div className="flex justify-start gap-4 mt-4">{buttons}</div>;

  async function handleSubmitClick() {
    // Show the policy preview modal with conflict detection
    const confirmed = await confirmPolicyWithPreview({
      policyId,
      policyName: values.name,
      policyType,
      environmentGroupIds: values.environmentGroupIds,
      originalEnvironmentGroupIds,
      groups,
    });

    if (confirmed) {
      submitForm();
    }
  }
}
